import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface FeedbackPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

interface FeedbackData {
  type: 'feedback' | 'concern' | 'suggestion';
  subject: string;
  message: string;
  rating?: number;
}

export default function FeedbackPopup({ isVisible, onClose }: FeedbackPopupProps) {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState<FeedbackData>({
    type: 'feedback',
    subject: '',
    message: '',
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Reset form when popup closes
  useEffect(() => {
    if (!isVisible) {
      setFormData({
        type: 'feedback',
        subject: '',
        message: '',
        rating: 5
      });
      setSubmitStatus('idle');
    }
  }, [isVisible]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      alert('Please log in to submit feedback');
      return;
    }

    if (!formData.message.trim()) {
      alert('Please enter your feedback message');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userName: currentUser.displayName || currentUser.email,
          type: formData.type,
          subject: formData.subject,
          message: formData.message,
          rating: formData.rating,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          currentPage: window.location.pathname
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FeedbackData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isVisible) return null;

  // Feedback type options with modern icons and colors
  const feedbackTypes = [
    { 
      value: 'feedback', 
      icon: '💝', 
      label: 'Feedback',
      description: 'Share your experience',
      gradient: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700'
    },
    { 
      value: 'concern', 
      icon: '⚠️', 
      label: 'Concern',
      description: 'Report an issue',
      gradient: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-700'
    },
    { 
      value: 'suggestion', 
      icon: '💡', 
      label: 'Suggestion',
      description: 'Help us improve',
      gradient: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-700'
    }
  ];

  return (
    <div className="fixed bottom-4 left-4 z-50 w-80 max-w-sm animate-slide-up">
      {/* Backdrop blur effect */}
      <div className="absolute inset-0 bg-white/20 backdrop-blur-sm rounded-2xl"></div>
      
      {/* Main container with glassmorphism */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 opacity-50"></div>
        
        {/* Header with compact design */}
        <div className="relative p-4 border-b border-gray-100/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  We'd love your feedback!
                </h3>
                <p className="text-xs text-gray-500 font-medium">Help us improve</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-all duration-200 group"
              aria-label="Close feedback popup"
            >
              <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="relative p-4">
          {submitStatus === 'success' ? (
            // Compact Success State
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg animate-bounce">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">Thank you! 🎉</h4>
              <p className="text-sm text-gray-600 leading-relaxed">Your feedback has been submitted successfully!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Compact Feedback Type Selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Feedback Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {feedbackTypes.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('type', option.value)}
                      className={`relative p-2 rounded-xl border-2 transition-all duration-300 text-center group hover:scale-[1.02] ${
                        formData.type === option.value
                          ? `${option.bgColor} ${option.borderColor} shadow-md scale-[1.02]`
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center space-y-1">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                          formData.type === option.value ? 'bg-white shadow-sm' : 'bg-gray-50'
                        }`}>
                          {option.icon}
                        </div>
                        <span className={`text-xs font-medium ${
                          formData.type === option.value ? option.textColor : 'text-gray-600'
                        }`}>
                          {option.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Compact Rating Section */}
              {formData.type === 'feedback' && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-3 border border-yellow-200">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Rate your experience
                  </label>
                  <div className="flex justify-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleInputChange('rating', star)}
                        className={`w-8 h-8 rounded-lg transition-all duration-200 transform hover:scale-110 ${
                          (formData.rating || 0) >= star
                            ? 'text-yellow-400 bg-yellow-100 shadow-sm'
                            : 'text-gray-300 hover:text-gray-400 hover:bg-gray-50'
                        }`}
                      >
                        <svg fill="currentColor" viewBox="0 0 20 20" className="w-5 h-5 mx-auto">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-xs text-gray-500 mt-1">
                    {formData.rating === 5 ? '🌟 Excellent!' : 
                     formData.rating === 4 ? '😊 Great!' :
                     formData.rating === 3 ? '👍 Good!' :
                     formData.rating === 2 ? '😐 Fair' : '😞 Poor'}
                  </p>
                </div>
              )}

              {/* Compact Subject Field */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Subject (optional)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Brief subject..."
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    maxLength={100}
                  />
                  <div className="absolute right-2 top-2 text-xs text-gray-400">
                    {formData.subject.length}/100
                  </div>
                </div>
              </div>

              {/* Compact Message Field */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Your message *
                </label>
                <div className="relative">
                  <textarea
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Share your thoughts..."
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200 resize-none"
                    maxLength={1000}
                    required
                  />
                  <div className="absolute right-2 bottom-2 text-xs text-gray-400">
                    {formData.message.length}/1000
                  </div>
                </div>
              </div>

              {/* Compact Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 flex items-center space-x-2">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-red-700 text-xs font-medium">
                    Something went wrong. Please try again.
                  </p>
                </div>
              )}

              {/* Compact Action Buttons */}
              <div className="flex space-x-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 border border-gray-200 rounded-xl hover:bg-gray-200 transition-all duration-200 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.message.trim()}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-1">
                      <svg className="animate-spin w-3 h-3 text-white" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                      </svg>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span>Send</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
} 